import * as React from 'react'
import { Drawer } from 'src/design-system'
import {
  ProfileSkillSidepanelProps,
  SuggestedSkill,
  SkillFormData,
} from './types'
import { AddSearchView } from './add-search-view'
import { CreateNewSkillView } from './create-new-skill-view'
import { SkillFormView } from './skill-form-view'
import { SkillView } from './skill-view'

export const ProfileSkillSidepanel: React.FC<ProfileSkillSidepanelProps> = ({
  mode,
  skill,
  userId,
  onClose,
  onModeChange,
  onAddSkill,
  onUpdateSkill,
  onDeleteSkill,
  onToggleStar,
  onDeclareSkill,
}) => {
  const [selectedSuggestion, setSelectedSuggestion] =
    React.useState<SuggestedSkill | null>(null)
  const [customSkillName, setCustomSkillName] = React.useState<string | null>(
    null
  )
  const [saving, setSaving] = React.useState(false)
  const drawerRef = React.useRef<HTMLDivElement>(null)

  const isOpen = mode !== 'closed'

  // Close on click outside
  React.useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement

      // Ignore clicks on Radix UI portaled content (dropdowns, dialogs, etc.)
      if (target.closest('[data-radix-popper-content-wrapper]')) {
        return
      }

      // Check if click is outside the drawer
      if (drawerRef.current && !drawerRef.current.contains(target)) {
        onClose()
      }
    }

    // Use mousedown for immediate response
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  // Reset state when mode changes
  React.useEffect(() => {
    if (mode !== 'add-detail') {
      setSelectedSuggestion(null)
    }
    if (mode !== 'create-new-detail') {
      setCustomSkillName(null)
    }
  }, [mode])

  const handleSelectSkill = (suggestion: SuggestedSkill) => {
    setSelectedSuggestion(suggestion)
    onModeChange('add-detail')
  }

  const handleBackFromAddDetail = () => {
    setSelectedSuggestion(null)
    onModeChange('add')
  }

  const handleCreateNewSkill = () => {
    onModeChange('create-new')
  }

  const handleBackFromCreateNew = () => {
    onModeChange('add')
  }

  const handleCreateSkillSubmit = (skillName: string) => {
    setCustomSkillName(skillName)
    onModeChange('create-new-detail')
  }

  const handleBackFromCreateNewDetail = () => {
    setCustomSkillName(null)
    onModeChange('create-new')
  }

  const handleAddSave = async (data: SkillFormData) => {
    setSaving(true)
    try {
      await onAddSkill(data)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const handleEditSave = async (data: SkillFormData) => {
    if (!skill) return
    setSaving(true)
    try {
      await onUpdateSkill(skill.profileSkillId, data)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (_skillId: string) => {
    onModeChange('edit')
  }

  const handleDeclare = (skillId: string) => {
    // Just declare the skill (change source to self_declared), stay in view mode
    onDeclareSkill?.(skillId)
  }

  const getTitle = () => {
    switch (mode) {
      case 'add':
        return 'Add skill'
      case 'add-detail':
        return selectedSuggestion?.name || 'Add skill'
      case 'create-new':
        return 'Create new personal skill'
      case 'create-new-detail':
        return customSkillName || ''
      case 'view':
      case 'edit':
        return skill?.name || ''
      default:
        return ''
    }
  }

  const renderContent = () => {
    switch (mode) {
      case 'add':
        return (
          <AddSearchView
            onClose={onClose}
            onSelectSkill={handleSelectSkill}
            onCreateNewSkill={handleCreateNewSkill}
          />
        )

      case 'add-detail':
        if (!selectedSuggestion) return null
        return (
          <SkillFormView
            name={selectedSuggestion.name}
            initialDescription={selectedSuggestion.description || ''}
            initialSkillType={
              (selectedSuggestion.type as 'hard' | 'soft') || null
            }
            initialProficiencyLevel={null}
            initialStarred={false}
            canonicalDescription={selectedSuggestion.description || ''}
            canonicalSkillType={
              (selectedSuggestion.type as 'hard' | 'soft') || null
            }
            showBackButton={true}
            onBack={handleBackFromAddDetail}
            onClose={onClose}
            onSave={handleAddSave}
            saving={saving}
          />
        )

      case 'create-new':
        return (
          <CreateNewSkillView
            onBack={handleBackFromCreateNew}
            onClose={onClose}
            onCreateSkill={handleCreateSkillSubmit}
          />
        )

      case 'create-new-detail':
        if (!customSkillName) return null
        return (
          <SkillFormView
            name={customSkillName}
            initialDescription=""
            initialSkillType={null}
            initialProficiencyLevel={null}
            initialStarred={false}
            showBackButton={true}
            onBack={handleBackFromCreateNewDetail}
            onClose={onClose}
            onSave={handleAddSave}
            saving={saving}
          />
        )

      case 'view':
        if (!skill) return null
        return (
          <SkillView
            skill={skill}
            onClose={onClose}
            onDelete={onDeleteSkill}
            onEdit={handleEdit}
            onDeclare={handleDeclare}
            onToggleStar={onToggleStar}
          />
        )

      case 'edit':
        if (!skill) return null
        return (
          <SkillFormView
            name={skill.name}
            initialDescription={skill.description || ''}
            initialSkillType={(skill.skillType as 'hard' | 'soft') || null}
            initialProficiencyLevel={skill.proficiencyLevel}
            initialStarred={skill.starred}
            showBackButton={false}
            onClose={onClose}
            onSave={handleEditSave}
            saving={saving}
            skillId={skill.id}
            userId={userId}
          />
        )

      default:
        return null
    }
  }

  return (
    <Drawer
      title={getTitle()}
      open={isOpen}
      onOpen={() => {}}
      onClose={onClose}
      direction="right"
      overlay={false}
      className="w-[480px] sm:w-[520px]"
    >
      <div ref={drawerRef} className="h-full">
        {renderContent()}
      </div>
    </Drawer>
  )
}
